/**
 * Light/dark theming.
 *
 * Three states, not two. "system" is the default and is genuinely different from picking light: it
 * follows the device and keeps following it, so someone whose phone flips to dark at sunset gets the
 * app flipping with it. Choosing light or dark is an override that stops tracking the device.
 *
 * The resolved theme lands on `<html data-theme>`, which is what `index.css` keys off. Nothing else in
 * the app needs to know the theme -- every colour is a variable defined per theme in one place.
 */
export type ThemePreference = "system" | "light" | "dark";
export type ResolvedTheme = "light" | "dark";

const STORAGE_KEY = "aa-theme";

export function getThemePreference(): ThemePreference {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    return stored === "light" || stored === "dark" || stored === "system" ? stored : "system";
  } catch {
    // Private browsing, or storage disabled entirely. A theme preference is not worth an exception.
    return "system";
  }
}

function systemTheme(): ResolvedTheme {
  return typeof matchMedia === "function" && matchMedia("(prefers-color-scheme: dark)").matches ? "dark" : "light";
}

export function resolveTheme(preference: ThemePreference): ResolvedTheme {
  return preference === "system" ? systemTheme() : preference;
}

export function applyTheme(preference: ThemePreference): void {
  document.documentElement.setAttribute("data-theme", resolveTheme(preference));
}

export function setThemePreference(preference: ThemePreference): void {
  try {
    localStorage.setItem(STORAGE_KEY, preference);
  } catch {
    // Still apply it for this session even if it can't be remembered for the next one.
  }
  applyTheme(preference);
}

/**
 * Keeps a "system" preference live.
 *
 * Without this, following the device would mean following it *as of page load* -- the app would stay
 * light through a sunset the phone had already reacted to, until the next refresh. An explicit light or
 * dark choice deliberately ignores the device, so the listener does nothing in those states.
 */
export function watchSystemTheme(): () => void {
  if (typeof matchMedia !== "function") return () => {};
  const query = matchMedia("(prefers-color-scheme: dark)");
  const onChange = () => {
    if (getThemePreference() === "system") applyTheme("system");
  };
  query.addEventListener("change", onChange);
  return () => query.removeEventListener("change", onChange);
}
